/**
 * dada-sport-arguments.js
 *
 * Onglets de la section « Dada Sport — Arguments ».
 * Un clic sur un titre bascule le texte affiche en dessous et l'image de droite.
 *
 * Motif ARIA « tabs » vertical : un seul onglet reste dans l'ordre de tabulation,
 * les fleches circulent entre les onglets, Home / End vont aux extremites.
 *
 * Le premier onglet est deja marque actif par le Liquid : sans JS la section
 * reste lisible, elle perd juste la bascule.
 *
 * Mobile: the tabs give way to a `<details>` accordion. Opening an item goes
 * through the same `#select` as a tab: it closes the other items (fallback for
 * browsers ignoring `name`) and shows its image.
 */

class DadaSportArguments extends HTMLElement {
  /** @type {HTMLElement[]} */
  #tabs = [];

  /**
   * Textes et images confondus : les deux portent `data-arguments-panel`, donc
   * une seule boucle suffit a synchroniser les deux colonnes.
   * @type {HTMLElement[]}
   */
  #targets = [];

  /** @type {HTMLDetailsElement[]} */
  #items = [];

  connectedCallback() {
    this.#tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-arguments-tab]')]);
    this.#targets = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-arguments-panel]')]);
    this.#items = /** @type {HTMLDetailsElement[]} */ ([...this.querySelectorAll('[data-arguments-item]')]);

    // Un seul onglet : rien a basculer, on laisse l'etat rendu par le serveur.
    if (this.#tabs.length < 2) return;

    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#handleClick);
      tab.addEventListener('keydown', this.#handleKeydown);
    }

    // `toggle` does not bubble: one listener per item.
    for (const item of this.#items) {
      item.addEventListener('toggle', this.#handleToggle);
    }
  }

  disconnectedCallback() {
    for (const tab of this.#tabs) {
      tab.removeEventListener('click', this.#handleClick);
      tab.removeEventListener('keydown', this.#handleKeydown);
    }

    for (const item of this.#items) {
      item.removeEventListener('toggle', this.#handleToggle);
    }
  }

  /** @param {Event} event */
  #handleToggle = (event) => {
    const item = event.currentTarget;
    // Closing an item changes nothing: the last image stays in place.
    if (!(item instanceof HTMLDetailsElement) || !item.open || !item.dataset.argumentsItem) return;

    this.#select(item.dataset.argumentsItem);
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement) || !tab.dataset.argumentsTab) return;

    this.#select(tab.dataset.argumentsTab);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const { key } = event;
    const step = key === 'ArrowDown' || key === 'ArrowRight' ? 1 : key === 'ArrowUp' || key === 'ArrowLeft' ? -1 : 0;

    if (step === 0 && key !== 'Home' && key !== 'End') return;

    // `findIndex` sur l'element focalise plutot que sur l'onglet actif : le focus
    // peut se promener sans selectionner (clic puis Tab retour).
    const currentIndex = this.#tabs.findIndex((tab) => tab === event.currentTarget);
    if (currentIndex === -1) return;

    // Les fleches feraient defiler la page, Home / End sauteraient en haut ou en bas.
    event.preventDefault();

    let nextIndex = currentIndex;
    if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = this.#tabs.length - 1;
    } else {
      nextIndex = (currentIndex + step + this.#tabs.length) % this.#tabs.length;
    }

    const name = this.#tabs[nextIndex]?.dataset.argumentsTab;
    if (name) this.#select(name, { focus: true });
  };

  /**
   * @param {string} name
   * @param {object} [options]
   * @param {boolean} [options.focus] - Deplace le focus sur l'onglet (navigation clavier).
   */
  #select(name, options = {}) {
    for (const tab of this.#tabs) {
      const isActive = tab.dataset.argumentsTab === name;

      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      if (isActive && options.focus) tab.focus();
    }

    for (const target of this.#targets) {
      target.classList.toggle('is-active', target.dataset.argumentsPanel === name);
    }

    // Keeps the mobile accordion in step with the tabs, so crossing 750px (a tablet
    // rotated) never shows one item open next to another item's image. Closing the
    // other items fires `toggle` with `open: false`, which the handler ignores.
    for (const item of this.#items) {
      item.open = item.dataset.argumentsItem === name;
    }
  }
}

if (!customElements.get('dada-sport-arguments')) {
  customElements.define('dada-sport-arguments', DadaSportArguments);
}
